import React, { useEffect } from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { BrowserRouter } from 'react-router-dom';
import { MatomoProvider, createInstance } from '@mitresthen/matomo-tracker-react';
import { isProduction } from './utils/envUtils';

// Create Matomo instance
// Configuration from environment variables with fallback to provided URLs
const matomoUrlBase = process.env.REACT_APP_MATOMO_URL_BASE || 'https://mindfultherdev.wpenginepowered.com/';
const matomoSiteId = parseInt(process.env.REACT_APP_MATOMO_SITE_ID || '1', 10);
const matomoTrackerUrl = process.env.REACT_APP_MATOMO_TRACKER_URL || 'https://mindfultherdev.wpenginepowered.com/wp-content/plugins/matomo/app/matomo.php';
const matomoSrcUrl = process.env.REACT_APP_MATOMO_SRC_URL || 'https://mindfultherdev.wpenginepowered.com/wp-content/uploads/matomo/matomo.js';

// Only create Matomo instance if URL is configured and we're in production
const matomoInstance = (matomoUrlBase && isProduction()) ? createInstance({
  urlBase: matomoUrlBase, // Must end with /
  siteId: matomoSiteId,
  trackerUrl: matomoTrackerUrl,
  srcUrl: matomoSrcUrl,
  configurations: {
    disableCookies: false, // Set true if you want to disable cookies
  },
}) : null;

// Initialize Google Tag Manager
const gtmId = process.env.REACT_APP_GTM_ID || 'GTM-PQLFM32F';

// Component to initialize GTM
function GTMInitializer() {
  useEffect(() => {
    // Initialize dataLayer
    (window as any).dataLayer = (window as any).dataLayer || [];
    
    // Add GTM script to head
    const script = document.createElement('script');
    script.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${gtmId}');`;
    document.head.appendChild(script);
    
    // Freshpaint Start - Only load in production (runtime check)
    if (isProduction()) {
      const freshpaintInitScript = document.createElement('script');
      freshpaintInitScript.type = 'text/javascript';
      freshpaintInitScript.textContent = `(function(){function p(r,e){(e==null||e>r.length)&&(e=r.length);for(var t=0,a=new Array(e);t<e;t++)a[t]=r[t];return a}function v(r){if(Array.isArray(r))return p(r)}function h(r){if(typeof Symbol!="undefined"&&r[Symbol.iterator]!=null||r["@@iterator"]!=null)return Array.from(r)}function A(){throw new TypeError("Invalid attempt to spread non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.")}function I(r,e){if(r){if(typeof r=="string")return p(r,e);var t=Object.prototype.toString.call(r).slice(8,-1);if(t==="Object"&&r.constructor&&(t=r.constructor.name),t==="Map"||t==="Set")return Array.from(t);if(t==="Arguments"||/^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t))return p(r,e)}}function y(r){return v(r)||h(r)||I(r)||A()}var E=function(r,e){if(!e.__SV){try{var t,a,m=window.location,c=m.hash,x=function(n,o){return t=n.match(new RegExp(o+"=([^&]*)")),t?t[1]:null};c&&x(c,"fpState")&&(a=JSON.parse(decodeURIComponent(x(c,"fpState"))),a.action==="fpeditor"&&(window.sessionStorage.setItem("_fpcehash",c),history.replaceState(a.desiredHash||"",r.title,m.pathname+m.search)))}catch(S){}e.__loaded=!1,e.config=!1,e.__SV=2,window.freshpaint=new Proxy(e,{get:function(n,o){return n[o]!==void 0?n[o]:o==="init"?function(l,u,i){var _,d;(_=n)[d="_i"]||(_[d]=[]),n._i.push([l,u||{},i||"freshpaint"])}:function(){for(var l=arguments.length,u=new Array(l),i=0;i<l;i++)u[i]=arguments[i];var _=[o].concat(y(u));return n.push(_),new Proxy(_,{get:function(f,w){return f[w]?f[w]:function(){for(var b=arguments.length,g=new Array(b),s=0;s<b;s++)g[s]=arguments[s];f.length=0,f.push([o].concat(y(u))),f.push([w].concat(y(g)))}}})}}})}};E(document,window.freshpaint||[]);})();
    freshpaint.init("1f7318a7-89e1-432e-a577-17b98648f8a0");
    freshpaint.page();`;
      document.head.appendChild(freshpaintInitScript);
      
      const freshpaintScript = document.createElement('script');
      freshpaintScript.src = 'https://freshpaint-cdn.com/js/1f7318a7-89e1-432e-a577-17b98648f8a0/freshpaint.js';
      freshpaintScript.async = true;
      document.head.appendChild(freshpaintScript);
    }
    // Freshpaint End
    
    // Add noscript iframe to body (if not already present)
    if (!document.getElementById('gtm-noscript')) {
      const noscript = document.createElement('noscript');
      noscript.id = 'gtm-noscript';
      const iframe = document.createElement('iframe');
      iframe.src = `https://www.googletagmanager.com/ns.html?id=${gtmId}`;
      iframe.height = '0';
      iframe.width = '0';
      iframe.style.display = 'none';
      iframe.style.visibility = 'hidden';
      noscript.appendChild(iframe);
      document.body.insertBefore(noscript, document.body.firstChild);
    }
  }, []);
  
  return null;
}

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

// Render with MatomoProvider if configured, otherwise render without it
const appContent = (
  <BrowserRouter future={{ 
    v7_startTransition: true,
    v7_relativeSplatPath: true 
  }}>
    <App />
  </BrowserRouter>
);

root.render(
  <React.StrictMode>
    <GTMInitializer />
    {matomoInstance ? (
      <MatomoProvider value={matomoInstance}>
        {appContent}
      </MatomoProvider>
    ) : (
      appContent
    )}
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
